<template>
  <v-card class="mb-4">
    <v-card-title class="text-subtitle-1">
      {{ $t('onboarding.commissioning.activation.title') }}
      <v-chip v-if="plan" x-small class="ml-2" :color="planColor" dark>
        {{ $t(`onboarding.registration.plan-status.${plan.status}`) }}
      </v-chip>
    </v-card-title>

    <v-card-text>
      <v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>

      <!-- 0. Pas encore : la décision, la personne ou le paiement manquent. La pose se lit, grisée. -->
      <v-alert v-if="!plan && !order.readyForInstallation" type="info" dense text class="mb-3">
        {{ $t('onboarding.commissioning.activation.not-ready') }}
      </v-alert>

      <v-alert v-if="!plan && order.readyForInstallation && (order.notBlockingRequirements || []).length" type="info" dense text class="mb-3">
        {{ $t('onboarding.commissioning.activation.not-blocking', { requirements: order.notBlockingRequirements.map(requirementLabel).join(', ') }) }}
      </v-alert>

      <!-- 1. L'équipement que l'on a en main, et où il est posé si la commande ne le dit pas. -->
      <template v-if="!preview && !plan">
        <p class="text-caption text--secondary mb-2">{{ $t('onboarding.commissioning.activation.explained') }}</p>
        <p v-if="order.preparedEquipmentId" class="text-caption mb-2">{{ $t('onboarding.commissioning.activation.prepared') }}</p>
        <v-row align="center">
          <v-col cols="12" md="6">
            <v-text-field
                v-model="identifier"
                :label="isChargingPoint ? $t('onboarding.commissioning.activation.identifier') : $t('onboarding.commissioning.activation.meter-device')"
                :prepend-inner-icon="isChargingPoint ? 'mdi-ev-station' : 'mdi-meter-electric-outline'"
                outlined dense hide-details
                :disabled="!order.readyForInstallation"
                @keyup.enter="check"
            />
          </v-col>
        </v-row>

        <template v-if="needsLocation">
          <div class="text-subtitle-2 mt-4 mb-2">{{ $t('onboarding.commissioning.activation.where') }}</div>
          <v-radio-group v-model="locationMode" row dense hide-details class="mt-0 mb-2">
            <v-radio value="existing" :label="$t('onboarding.commissioning.form.location-existing')" />
            <v-radio value="new" :label="$t('onboarding.commissioning.form.location-new')" />
          </v-radio-group>
          <v-select v-if="locationMode === 'existing'" v-model="locationId" :items="locations" item-text="name" item-value="id"
                    :label="$t('onboarding.commissioning.location')" outlined dense hide-details />
          <new-location-picker v-else v-model="newPlace" :organization-id="order.organizationId" :label="$t('onboarding.commissioning.form.address')" required />
        </template>

        <v-btn small color="primary" outlined class="mt-4" :loading="working" :disabled="!canCheck || !order.readyForInstallation" @click="check">
          {{ $t('onboarding.commissioning.activation.check') }}
        </v-btn>
      </template>

      <!-- 2. Lequel, et ce qui va être fait : on confirme. -->
      <template v-else-if="preview && !plan">
        <v-alert type="info" outlined dense class="mb-3">
          <div class="font-weight-bold">{{ preview.equipment.identifier || preview.equipment.id }}<span v-if="preview.equipment.name"> · {{ preview.equipment.name }}</span></div>
          <div class="text-caption">
            {{ $t('onboarding.commissioning.activation.currently', {
              status: chargingPointStatusLabel(preview.equipment.status),
              location: preview.equipment.locationName || $t('onboarding.commissioning.activation.no-location'),
            }) }}
          </div>
        </v-alert>
        <reassignment-warning v-if="preview.reassignment" v-model="reassignmentConfirmed" :reassignment="preview.reassignment" />
        <plan-steps :steps="preview.plan.steps" />
        <div class="d-flex mt-3">
          <v-btn small text :disabled="working" @click="preview = null">{{ $t('onboarding.commissioning.activation.other') }}</v-btn>
          <v-spacer />
          <v-btn small color="primary" :loading="working" :disabled="!!preview.reassignment && !reassignmentConfirmed" @click="activate({ ...onSiteBody, confirmReassignment: !!preview.reassignment && reassignmentConfirmed })">
            {{ $t('onboarding.commissioning.activation.confirm') }}
          </v-btn>
        </div>
      </template>

      <!-- 3. Ce qui a été fait ; un échec dit quoi corriger, et le même plan reprend. -->
      <template v-else>
        <plan-steps :steps="plan.steps" />
        <div v-if="plan.status !== 'completed'" class="d-flex mt-3">
          <v-spacer />
          <v-btn small color="primary" :loading="working" @click="activate(plannedOnSite)">
            <v-icon left small>mdi-play</v-icon>{{ $t('onboarding.registration.resume') }}
          </v-btn>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import LocationRepository from '@repository/LocationRepository'
import CommissioningMixin from './CommissioningMixin'
import NewLocationPicker from './NewLocationPicker.vue'
import PlanSteps from './PlanSteps.vue'
import ReassignmentWarning from './ReassignmentWarning.vue'

/**
 * Poser l'équipement depuis le back-office : son identifiant (ou rien, s'il a été préparé), où le
 * poser quand la commande ne le dit pas, la confirmation (quel équipement, où il est aujourd'hui,
 * ce qui va être fait), puis le plan qui s'exécute sous les yeux. Un plan arrêté se reprend tel quel.
 */
export default {
  name: 'CommissioningActivation',

  components: { NewLocationPicker, PlanSteps, ReassignmentWarning },

  mixins: [CommissioningMixin],

  props: {
    order: { type: Object, required: true },
  },

  data() {
    return {
      identifier: '',
      locationMode: 'existing',
      locationId: null,
      newPlace: null,
      locations: [],
      preview: null,
      plan: this.order.plan || null,
      working: false,
      error: null,
      reassignmentConfirmed: false,
    }
  },

  computed: {
    isChargingPoint() {
      return this.order.kind === 'charging-point'
    },

    /** Une commande sans emplacement : on le désigne ici (pour un membre, son adresse suffit quand on la connaît). */
    needsLocation() {
      if (this.order.locationId || this.order.newLocation) return false
      return this.order.holder === 'organization' || !this.order.person?.postalAddress
    },

    canCheck() {
      const equipmentKnown = this.identifier.trim() !== '' || !!this.order.preparedEquipmentId
      if (!this.needsLocation) return equipmentKnown
      return equipmentKnown && (this.locationMode === 'existing' ? !!this.locationId : !!(this.newPlace?.locationId || this.newPlace?.newLocation?.street))
    },

    /** Ce qui est dit sur place. */
    onSiteBody() {
      const body = {}
      const value = this.identifier.trim()
      if (value) body[this.isChargingPoint ? 'identifier' : 'meterDeviceId'] = value
      if (this.needsLocation) {
        if (this.locationMode === 'existing') {
          body.locationId = this.locationId
        } else if (this.newPlace?.locationId) {
          body.locationId = this.newPlace.locationId
        } else {
          body.newLocation = this.newPlace?.newLocation
        }
      }
      return body
    },

    /** Reprendre un plan arrêté : le même équipement, relu dans le geste qui le porte. */
    plannedOnSite() {
      const params = (this.plan?.steps || []).map(step => step.params || {})
      const identifier = params.find(p => p.identifier)?.identifier
      const meterDeviceId = params.find(p => p.meterDeviceId)?.meterDeviceId
      return identifier ? { identifier } : (meterDeviceId ? { meterDeviceId } : {})
    },

    planColor() {
      if (this.plan?.status === 'completed') return 'green'
      if (this.plan?.status === 'failed') return 'red'
      return 'orange'
    },
  },

  created() {
    if (this.needsLocation) {
      LocationRepository.search({ organizationId: this.order.organizationId, offset: 0, limit: 200, all: true }, 'commissioning-activation')
        .then(result => { this.locations = (result.items || []).map(l => ({ id: l.id, name: l.name || l.id })) })
        .catch(() => { this.locations = [] })
    }
  },

  methods: {
    async check() {
      await this.run(async () => {
        this.reassignmentConfirmed = false
        this.preview = await CommissioningRepository.activationPreview(this.order.id, this.asQuery(this.onSiteBody))
      })
    },

    async activate(onSite) {
      await this.run(async () => {
        const result = await CommissioningRepository.activate(this.order.id, onSite)
        this.plan = result.plan
        this.preview = null
        this.$emit('updated', result.order)
      })
    },

    async run(action) {
      this.working = true
      this.error = null
      try {
        await action()
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },
  },
}
</script>
